import { useState } from "react";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";
import { EntityForm, Field } from "./EntityForm";
import { PackEditor } from "./PackEditor";
import { ServiceEditor, ServiceRow } from "./ServiceEditor";

/**
 * Admin CRUD for the onboarding managed lists — teams, departments, branches,
 * responsibilities, pay types, overtime eligibilities, document types and policy
 * packs. Every onboarding dropdown reads from these, so they are "editable in
 * settings, not hardcoded". Each list reuses EntityForm against its REST resource.
 */

interface Row { id: string; name: string; [k: string]: unknown; }

interface ListConfig {
    title: string;
    path: string;
    fields: Field[];
    /** Extra flags to show beside a row's name. */
    badge?: (row: Row) => string | null;
    /** Rows without a name column (e.g. FAQs) say what to display instead. */
    labelOf?: (row: Row) => string;
    /** Edit opens the full onboarding-pack editor instead of the plain form. */
    usePackEditor?: boolean;
    /** Edit opens the NDIS service charge editor (waterfall) instead of the plain form. */
    useServiceEditor?: boolean;
}

const nameField: Field = { name: "name", label: "Name", kind: "text", required: true };
const activeField: Field = { name: "is_active", label: "Active", kind: "checkbox" };

const LISTS: ListConfig[] = [
    { title: "Teams", path: "/teams", fields: [nameField, activeField] },
    { title: "Departments", path: "/departments", fields: [nameField, activeField] },
    { title: "Branches", path: "/branches", fields: [nameField, activeField] },
    { title: "Responsibilities", path: "/responsibilities", fields: [nameField, activeField] },
    { title: "Pay types", path: "/pay-types", fields: [nameField, activeField] },
    { title: "Overtime eligibilities", path: "/overtime-eligibilities", fields: [nameField, activeField] },
    {
        title: "Document types", path: "/document-types",
        fields: [
            nameField,
            { name: "expires", label: "Requires an expiry date", kind: "checkbox" },
            { name: "is_required_default", label: "Required for onboarding", kind: "checkbox" },
            { name: "sort_order", label: "Sort order", kind: "number", half: true },
            activeField,
        ],
        badge: (r) => [r.expires ? "expires" : null, r.is_required_default ? "required" : null].filter(Boolean).join(" · ") || null,
    },
    {
        title: "Onboarding packs", path: "/policy-packs",
        fields: [nameField, { name: "description", label: "Description", kind: "textarea" }, activeField],
        usePackEditor: true,
        badge: (r) => (r.is_default ? "default" : null),
    },
];

const sortField: Field = { name: "sort_order", label: "Sort order", kind: "number", half: true };
const categoryField: Field = { name: "category", label: "Category", kind: "text", half: true };

/**
 * The platform-wide vocabulary lists (041). Every dropdown that used to read a
 * hardcoded array now reads one of these, so each is seeded, editable and
 * "add-anywhere". Job roles and services are richer; the rest are plain
 * name + sort-order lists. The three training lists start empty and fill as
 * staff type values into the training form.
 */
const VOCAB_LISTS: ListConfig[] = [
    {
        title: "Job roles", path: "/roles",
        fields: [nameField, categoryField,
            { name: "base_hourly_rate", label: "Base pay rate ($/hr)", kind: "number", half: true, min: 0,
              help: "Indicative pay rate for this role — used in the margin preview." },
            { name: "billing_role", label: "NDIS billing item", kind: "select", half: true,
              options: [
                  { value: "", label: "— not billed to NDIS —" },
                  { value: "EN", label: "Enrolled Nurse (EN)" },
                  { value: "RN", label: "Registered Nurse (RN)" },
                  { value: "CN", label: "Clinical Nurse (CN)" },
                  { value: "CNC", label: "Clinical Nurse Consultant (CNC)" },
                  { value: "NP", label: "Nurse Practitioner (NP)" },
              ],
              help: "Which of the 5 NDIS nursing items this role bills under. A CNS or CNK is paid at their own rate but bills under CN or RN." },
            { name: "ndis_reg_prefix", label: "Budget group", kind: "select", half: true,
              options: [
                  { value: "01", label: "Core — Assistance with Daily Life" },
                  { value: "15", label: "Capacity Building — Improved Daily Living" },
              ] },
            { name: "charge_percent", label: "Your charge (% of cap)", kind: "number", half: true, min: 0, max: 100,
              help: "What to bill a provider, as a % of the NDIS cap. Default 88%." },
            activeField],
        badge: (r) => r.billing_role
            ? `bills as ${String(r.billing_role)} · ${r.charge_percent ?? 88}% of cap`
            : (r.category ? String(r.category) : null),
    },
    {
        title: "Services", path: "/services",
        useServiceEditor: true,
        fields: [nameField, categoryField,
            { name: "default_rate", label: "Default rate ($)", kind: "number", half: true, min: 0,
              help: "Prefilled onto a quote line when this service is picked (editable per line). Leave blank if unset." },
            { name: "unit", label: "Unit", kind: "text", half: true, placeholder: "hour" },
            { name: "description", label: "Description", kind: "textarea" }, sortField, activeField],
        badge: (r) => [r.category ? String(r.category) : null,
            r.billing_role ? `${r.billing_role} · ${r.charge_percent ?? 88}% of cap` : null,
            r.default_rate != null ? `$${r.default_rate}/${r.unit ?? "hour"}` : null].filter(Boolean).join(" · ") || null,
    },
    { title: "Opportunities", path: "/opportunity-types", fields: [nameField, sortField, activeField] },
    { title: "Lead sources", path: "/lead-sources", fields: [nameField, sortField, activeField] },
    { title: "Industries", path: "/industries", fields: [nameField, sortField, activeField] },
    { title: "Job titles", path: "/job-titles", fields: [nameField, sortField, activeField] },
    { title: "Disabilities", path: "/disability-types", fields: [nameField, sortField, activeField] },
    { title: "Participant conditions", path: "/participant-conditions", fields: [nameField, sortField, activeField] },
    { title: "Location types", path: "/location-types", fields: [nameField, sortField, activeField] },
    { title: "Service regions", path: "/service-regions", fields: [nameField, sortField, activeField] },
    {
        title: "Help centre FAQ", path: "/faqs",
        labelOf: (r) => String((r as Record<string, unknown>).question ?? ""),
        badge: (r) => {
            const row = r as Record<string, unknown>;
            return [String(row.category ?? ""),
                row.audience && row.audience !== "all" ? String(row.audience) : null,
                row.is_published === false ? "draft" : null].filter(Boolean).join(" · ") || null;
        },
        fields: [
            { name: "question", label: "Question", kind: "text", required: true },
            { name: "answer", label: "Answer", kind: "textarea", required: true },
            { name: "category", label: "Category", kind: "text", required: true, half: true,
              suggestions: ["Getting started", "Referrals", "Participants", "Invoices", "Messages", "Accounts", "Rostering", "Pay"] },
            { name: "audience", label: "Audience", kind: "select", half: true, options: [
                { value: "all", label: "Everyone" },
                { value: "internal", label: "Staff only" },
                { value: "partner", label: "Providers only" },
            ] },
            sortField,
            { name: "is_published", label: "Published (visible in the help centre)", kind: "checkbox" },
        ],
    },
    {
        title: "Referral services", path: "/referral-services",
        fields: [nameField,
            { name: "is_high_intensity", label: "High-intensity (HIDPA) support — requires signed-off workers", kind: "checkbox" },
            sortField, activeField],
        badge: (r) => (r.is_high_intensity ? "high intensity" : null),
    },
    { title: "Competency categories", path: "/competency-categories", fields: [nameField, sortField, activeField] },
    { title: "Training providers", path: "/training-providers", fields: [nameField, sortField, activeField] },
    { title: "Assessors", path: "/training-assessors", fields: [nameField, sortField, activeField] },
    { title: "Courses", path: "/training-courses", fields: [nameField, sortField, activeField] },
];

function ListEditor({ config }: { config: ListConfig }) {
    const rows = useResource<Row[]>(config.path);
    const [editing, setEditing] = useState<Row | "new" | null>(null);
    const [query, setQuery] = useState("");

    const remove = async (id: string) => {
        await api.delete(`${config.path}/${id}`);
        rows.reload();
    };

    const all = rows.data ?? [];
    const labelFor = (r: Row) => (config.labelOf ? config.labelOf(r) : r.name);
    const visible = query.trim()
        ? all.filter((r) => labelFor(r).toLowerCase().includes(query.trim().toLowerCase()))
        : all;

    return (
        <div className="managed-list">
            <div className="managed-list-head">
                <h3>{config.title} <span className="muted" style={{ fontWeight: 400 }}>· {all.length}</span></h3>
                <button className="btn ghost small" onClick={() => setEditing("new")}>＋ Add</button>
            </div>
            {all.length > 12 && (
                <input
                    type="search" value={query} placeholder={`Filter ${all.length} entries…`}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ marginBottom: "0.5rem", width: "100%" }}
                />
            )}
            <ul className="managed-list-items">
                {all.length === 0 && <li className="muted">No entries yet.</li>}
                {all.length > 0 && visible.length === 0 && <li className="muted">Nothing matches "{query}".</li>}
                {visible.map((r) => {
                    const badge = config.badge?.(r);
                    return (
                        <li key={r.id}>
                            <span>{config.labelOf ? config.labelOf(r) : r.name}{badge && <span className="muted"> · {badge}</span>}
                                {r.is_active === false && <span className="chip mute" style={{ marginLeft: 6 }}>inactive</span>}</span>
                            <span className="managed-list-actions">
                                <button className="btn ghost small" onClick={() => setEditing(r)}>Edit</button>
                                <button className="btn ghost small" onClick={() => void remove(r.id)}>Delete</button>
                            </span>
                        </li>
                    );
                })}
            </ul>

            {editing && config.useServiceEditor ? (
                <ServiceEditor
                    service={editing === "new" ? null : (editing as unknown as ServiceRow)}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); rows.reload(); }}
                />
            ) : editing && config.usePackEditor && editing !== "new" ? (
                <PackEditor
                    pack={editing as unknown as { id: string; name: string; description?: string | null; required_admin_fields?: string[]; required_profile_fields?: string[] }}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); rows.reload(); }}
                />
            ) : editing ? (
                <EntityForm
                    title={editing === "new" ? `Add to ${config.title}` : `Edit ${config.labelOf ? config.labelOf(editing as Row) : (editing as Row).name}`}
                    path={config.path}
                    fields={config.fields}
                    initial={editing === "new" ? undefined : (editing as Row)}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); rows.reload(); }}
                />
            ) : null}
        </div>
    );
}

/**
 * Master–detail: an index of every managed list on the left, the one being
 * edited on the right. Only the open list fetches, and long catalogues get a
 * filter box — no more wall of twenty simultaneous lists.
 */
export function ManagedListsCard() {
    const groups = [
        { heading: "Platform vocabulary", lists: VOCAB_LISTS },
        { heading: "Onboarding", lists: LISTS },
    ];
    const [selectedPath, setSelectedPath] = useState(VOCAB_LISTS[0].path);
    const selected = [...VOCAB_LISTS, ...LISTS].find((c) => c.path === selectedPath) ?? VOCAB_LISTS[0];

    return (
        <div className="card">
            <div className="card-head">
                <h2>Lists &amp; dropdowns</h2>
                <p className="muted">Options for dropdowns across the platform. Pick a list to edit — changes appear everywhere the list is used.</p>
            </div>
            <div className="card-body list-manager">
                <nav className="list-manager-index" aria-label="Managed lists">
                    {groups.map((group) => (
                        <div key={group.heading} className="list-manager-group">
                            <div className="list-manager-heading">{group.heading}</div>
                            {group.lists.map((c) => (
                                <button key={c.path} className={c.path === selectedPath ? "active" : ""}
                                    onClick={() => setSelectedPath(c.path)}>
                                    {c.title}
                                </button>
                            ))}
                        </div>
                    ))}
                </nav>
                <div className="list-manager-detail">
                    <ListEditor key={selected.path} config={selected} />
                </div>
            </div>
        </div>
    );
}
